import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import type { Action } from './useGetOrdersFeed';

export interface HomeFeedOrder {
    _id: string;
    insurance: string | null;
    roNumber: string;
    emsId: string;
    vehicle: string;
    vin: string;
    location: string;
    daysActive: string;
    partsOrdered: number;
    totalParts: number;
    manuallyFlagged: boolean;
    reviewedByCount: number;
    reviewedByCurrentUser: boolean;
    completed: boolean;
    archived: boolean;
    canComplete: boolean;
    grossMargin: number | null;
    lastUpdated: Date;
    actions: Array<Action>;
    noteCount: number;
    supplierCommunicationCount: number;
    supplierChangesCount: number;
    scheduledDate?: Date | null;
}

/** Slugs sent in `POST /order/home/feed` `filters`; each matches its RO Manager tab label in kebab-case. */
export type HomeFeedFilter =
    | 'needs-quotes'
    | 'unordered-parts'
    | 'supplier-changes'
    | 'in-review'
    | 'completed'
    | 'manually-flagged'
    | 'totals-and-quotes'
    | 'scheduled';

/** Ordered-tab status filters (dashboard); applied server-side on orders feed when type is `ordered`. */
export type OrderOptionsFilter = 'confirmed' | 'partially-ordered' | 'all-ordered' | 'supplier-changes';

export interface AdditionalFilters {
    makes?: string[];
    lastUpdatedSort?: 'asc' | 'desc' | 'range';
    lastUpdatedRange?: { start: Date; end: Date; };
    daysActiveSort?: 'asc' | 'desc' | 'range';
    daysActiveRange?: { min: number; max: number; };
    quotingOptions?: 'onsite' | 'pre-order' | 'expedited';
    insuranceIds?: string[];
    last15Days?: boolean;  // false = disable 15-day filter, true/undefined = apply filter
    potentialTotal?: boolean;
    quotesOnly?: boolean;
    oemEndorsement?: boolean;
    hasDealerReferral?: boolean;
    /** Multi-select: primary statuses are OR’d; if `supplier-changes` is included, result must also have supplier changes. */
    orderOptions?: OrderOptionsFilter[];
}

export interface HomeFeedInputInput {
    limit: number;
    offset: number;
    locationIds: Array<string> | 'all';
    filters: Array<HomeFeedFilter>;
    search?: string;
    additionalFilters?: AdditionalFilters;
}

export interface HomeFeedResponse {
    orders: Array<HomeFeedOrder>;
}

const getHomeFeedPaginated = async (
    sessionId: string,
    input: HomeFeedInputInput
): Promise<HomeFeedResponse> => {
    const { data } = await axios.post(`${API_URL}/order/home/feed`,
        input,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useHomeFeedPaginated = (
    locationIds: Array<string> | 'all',
    page: number = 1,
    limit: number = 20,
    filters: Array<HomeFeedFilter>,
    search: string = '',
    additionalFilters: AdditionalFilters = {}
) => {
    const sessionContext = useContext(SessionContext);
    const offset = (page - 1) * limit;

    return useQuery({
        queryKey: [
            '/orders/home-feed-paginated',
            locationIds === 'all' ? 'all' : locationIds.join(','),
            page,
            limit,
            filters?.join(','),
            search,
            JSON.stringify(additionalFilters)
        ],
        queryFn: () =>
            getHomeFeedPaginated(sessionContext?.sessionId ?? '', {
                locationIds,
                limit,
                offset,
                filters,
                search,
                additionalFilters: Object.keys(additionalFilters).length > 0 ? additionalFilters : undefined
            }),
        enabled: !!sessionContext?.sessionId && (locationIds === 'all' || locationIds.length > 0),
        refetchInterval: 1000 * 60 * 5,
    });
};
